import React, { useState } from 'react';
import { Plus, Trash2, HelpCircle, BookOpen, Target, Globe, Users, Lightbulb } from 'lucide-react';
import { useTranslation } from '../../../contexts/TranslationContext';
import { Differentiation } from '../../../types';

interface DifferentiationSectionProps {
  differentiation: Differentiation;
  onUpdate: (differentiation: Differentiation) => void;
}

// Preset strategies for quick setup
const PRESET_SUPPORTS = {
  'visual_aids': 'Use visual aids like charts, diagrams, or manipulatives',
  'step_by_step': 'Break down complex tasks into smaller, manageable steps',
  'extra_time': 'Provide additional time for task completion',
  'peer_support': 'Pair with a peer for collaborative learning',
  'verbal_prompts': 'Use verbal prompts and cues to guide thinking',
  'reduced_complexity': 'Simplify problems or reduce the number of items',
  'hands_on': 'Provide hands-on, concrete learning experiences',
  'repetition': 'Allow for practice and repetition of concepts'
};

const PRESET_EXTENSIONS = {
  'challenge_problems': 'Provide more challenging or complex problems',
  'creative_expression': 'Allow creative expression of understanding',
  'research_project': 'Assign independent research or investigation',
  'peer_teaching': 'Have students teach concepts to peers',
  'advanced_concepts': 'Introduce advanced or related concepts',
  'real_world_app': 'Connect to real-world applications',
  'multiple_solutions': 'Encourage multiple solution strategies',
  'leadership_role': 'Assign leadership roles in group activities'
};

const PRESET_SENTENCE_FRAMES = [
  'I think ___ because ___.',
  'The answer is ___ because ___.',
  'I can solve this by ___.',
  'This reminds me of ___.',
  'I notice that ___.',
  'I wonder about ___.',
  'I agree/disagree because ___.',
  'The pattern I see is ___.'
];

const PRESET_ICONS = [
  'touch', 'point', 'count', 'write', 'draw', 'show', 'explain', 'think'
];

export const DifferentiationSection: React.FC<DifferentiationSectionProps> = ({
  differentiation,
  onUpdate
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'supports' | 'extensions' | 'ell'>('supports');
  const [showPresets, setShowPresets] = useState<Record<string, boolean>>({});

  const updateDifferentiation = (updates: Partial<Differentiation>) => {
    onUpdate({ ...differentiation, ...updates });
  };

  const addSupport = () => {
    const newSupports = { ...differentiation.supports };
    const key = `support_${Object.keys(newSupports).length + 1}`;
    newSupports[key] = '';
    updateDifferentiation({ supports: newSupports });
  };

  const updateSupport = (key: string, value: string) => {
    const newSupports = { ...differentiation.supports };
    newSupports[key] = value;
    updateDifferentiation({ supports: newSupports });
  };

  const removeSupport = (key: string) => {
    const newSupports = { ...differentiation.supports };
    delete newSupports[key];
    updateDifferentiation({ supports: newSupports });
  };

  const addPresetSupport = (key: string, value: string) => {
    const newSupports = { ...differentiation.supports };
    newSupports[key] = value;
    updateDifferentiation({ supports: newSupports });
    setShowPresets(prev => ({ ...prev, supports: false }));
  };

  const addExtension = () => {
    const newExtensions = { ...differentiation.extensions };
    const key = `extension_${Object.keys(newExtensions).length + 1}`;
    newExtensions[key] = '';
    updateDifferentiation({ extensions: newExtensions });
  };

  const updateExtension = (key: string, value: string) => {
    const newExtensions = { ...differentiation.extensions };
    newExtensions[key] = value;
    updateDifferentiation({ extensions: newExtensions });
  };

  const removeExtension = (key: string) => {
    const newExtensions = { ...differentiation.extensions };
    delete newExtensions[key];
    updateDifferentiation({ extensions: newExtensions });
  };

  const addPresetExtension = (key: string, value: string) => {
    const newExtensions = { ...differentiation.extensions };
    newExtensions[key] = value;
    updateDifferentiation({ extensions: newExtensions });
    setShowPresets(prev => ({ ...prev, extensions: false }));
  };

  const addSentenceFrame = () => {
    const newEll = { ...differentiation.ell };
    const sentenceFrames = [...(newEll.sentence_frames || [])];
    sentenceFrames.push('');
    updateDifferentiation({ ell: { ...newEll, sentence_frames: sentenceFrames } });
  };

  const updateSentenceFrame = (index: number, value: string) => {
    const newEll = { ...differentiation.ell };
    const sentenceFrames = [...(newEll.sentence_frames || [])];
    sentenceFrames[index] = value;
    updateDifferentiation({ ell: { ...newEll, sentence_frames: sentenceFrames } });
  };

  const removeSentenceFrame = (index: number) => {
    const newEll = { ...differentiation.ell };
    const sentenceFrames = [...(newEll.sentence_frames || [])];
    sentenceFrames.splice(index, 1);
    updateDifferentiation({ ell: { ...newEll, sentence_frames: sentenceFrames } });
  };

  const addPresetSentenceFrame = (frame: string) => {
    const newEll = { ...differentiation.ell };
    const sentenceFrames = [...(newEll.sentence_frames || []), frame];
    updateDifferentiation({ ell: { ...newEll, sentence_frames: sentenceFrames } });
    setShowPresets(prev => ({ ...prev, sentenceFrames: false }));
  };

  const addIcon = () => {
    const newEll = { ...differentiation.ell };
    const icons = [...(newEll.icons || [])];
    icons.push('');
    updateDifferentiation({ ell: { ...newEll, icons: icons } });
  };

  const updateIcon = (index: number, value: string) => {
    const newEll = { ...differentiation.ell };
    const icons = [...(newEll.icons || [])];
    icons[index] = value;
    updateDifferentiation({ ell: { ...newEll, icons: icons } });
  };

  const removeIcon = (index: number) => {
    const newEll = { ...differentiation.ell };
    const icons = [...(newEll.icons || [])];
    icons.splice(index, 1);
    updateDifferentiation({ ell: { ...newEll, icons: icons } });
  };

  const addPresetIcon = (icon: string) => {
    const newEll = { ...differentiation.ell };
    const icons = [...(newEll.icons || []), icon];
    updateDifferentiation({ ell: { ...newEll, icons: icons } });
    setShowPresets(prev => ({ ...prev, icons: false }));
  };

  const TabButton = ({ id, label, icon: Icon, count }: { id: string; label: string; icon: any; count: number }) => (
    <button
      onClick={() => setActiveTab(id as any)}
      className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
        activeTab === id
          ? 'bg-blue-100 text-blue-700 border-2 border-blue-200'
          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
      }`}
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
      {count > 0 && (
        <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
          {count}
        </span>
      )}
    </button>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{t('differentiation.differentiation')}</h3>
              <p className="text-sm text-gray-600">{t('differentiation.supportDiverseLearners')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <HelpCircle className="w-4 h-4" />
                          <span>{t('differentiation.configureStrategies')}</span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex space-x-2">
          <TabButton
            id="supports"
            label={t('differentiation.supports')}
            icon={Target}
            count={Object.keys(differentiation.supports || {}).length}
          />
          <TabButton
            id="extensions"
            label={t('differentiation.extensions')}
            icon={Lightbulb}
            count={Object.keys(differentiation.extensions || {}).length}
          />
          <TabButton
            id="ell"
            label={t('differentiation.ellSupport')}
            icon={Globe}
            count={(differentiation.ell?.sentence_frames?.length || 0) + (differentiation.ell?.icons?.length || 0)}
          />
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'supports' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-md font-medium text-gray-900">{t('differentiation.supportsForStrugglingLearners')}</h4>
                <p className="text-sm text-gray-600">{t('differentiation.strategiesForAdditionalSupport')}</p>
              </div>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => setShowPresets(prev => ({ ...prev, supports: !prev.supports }))}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <BookOpen className="w-4 h-4" />
                  <span>{t('differentiation.presets')}</span>
                </button>
                <button
                  type="button"
                  onClick={addSupport}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t('differentiation.addCustom')}</span>
                </button>
              </div>
            </div>

            {/* Preset Supports */}
            {showPresets.supports && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h5 className="text-sm font-medium text-blue-900 mb-3">{t('differentiation.quickAddPresetStrategies')}</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries(PRESET_SUPPORTS).map(([key, value]) => (
                    <button
                      key={key}
                      onClick={() => addPresetSupport(key, value)}
                      className="text-left p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <div className="text-sm font-medium text-blue-900">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
                      <div className="text-xs text-blue-700 mt-1">{value}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Custom Supports */}
            <div className="space-y-3">
              {Object.entries(differentiation.supports || {}).map(([key, value]) => (
                <div key={key} className="flex items-start space-x-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => updateSupport(key, e.target.value)}
                      placeholder={t('differentiation.describeSupportStrategy')}
                      className="input-field bg-white"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSupport(key)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {Object.keys(differentiation.supports || {}).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Target className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">{t('differentiation.noSupportStrategies')}</p>
                  <p className="text-xs mt-1">{t('differentiation.addStrategiesToHelp')}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'extensions' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-md font-medium text-gray-900">{t('differentiation.extensionsForAdvancedLearners')}</h4>
                <p className="text-sm text-gray-600">{t('differentiation.challengeActivitiesForStudents')}</p>
              </div>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => setShowPresets(prev => ({ ...prev, extensions: !prev.extensions }))}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <BookOpen className="w-4 h-4" />
                  <span>{t('differentiation.presets')}</span>
                </button>
                <button
                  type="button"
                  onClick={addExtension}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t('differentiation.addCustom')}</span>
                </button>
              </div>
            </div>

            {/* Preset Extensions */}
            {showPresets.extensions && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h5 className="text-sm font-medium text-purple-900 mb-3">{t('differentiation.quickAddPresetExtensions')}</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries(PRESET_EXTENSIONS).map(([key, value]) => (
                    <button
                      key={key}
                      onClick={() => addPresetExtension(key, value)}
                      className="text-left p-3 bg-white border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors"
                    >
                      <div className="text-sm font-medium text-purple-900">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
                      <div className="text-xs text-purple-700 mt-1">{value}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Custom Extensions */}
            <div className="space-y-3">
              {Object.entries(differentiation.extensions || {}).map(([key, value]) => (
                <div key={key} className="flex items-start space-x-3 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => updateExtension(key, e.target.value)}
                      placeholder={t('differentiation.describeExtensionActivity')}
                      className="input-field bg-white"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeExtension(key)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {Object.keys(differentiation.extensions || {}).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Lightbulb className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">{t('differentiation.noExtensionActivities')}</p>
                  <p className="text-xs mt-1">{t('differentiation.addChallengingActivities')}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'ell' && (
          <div className="space-y-6">
            <div>
              <h4 className="text-md font-medium text-gray-900">{t('differentiation.ellSupportFull')}</h4>
              <p className="text-sm text-gray-600">{t('differentiation.languageScaffolding')}</p>
            </div>
            
            {/* Sentence Frames */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="text-sm font-medium text-gray-700">{t('differentiation.sentenceFrames')}</h5>
                  <p className="text-xs text-gray-500">{t('differentiation.provideLanguageStructures')}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowPresets(prev => ({ ...prev, sentenceFrames: !prev.sentenceFrames }))}
                    className="btn-secondary flex items-center space-x-2"
                  >
                    <BookOpen className="w-4 h-4" />
                    <span>Presets</span>
                  </button>
                  <button
                    type="button"
                    onClick={addSentenceFrame}
                    className="btn-primary flex items-center space-x-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>{t('differentiation.addCustom')}</span>
                  </button>
                </div>
              </div>

              {/* Preset Sentence Frames */}
              {showPresets.sentenceFrames && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h6 className="text-sm font-medium text-orange-900 mb-3">{t('differentiation.quickAddSentenceFrames')}</h6>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {PRESET_SENTENCE_FRAMES.map((frame, index) => (
                      <button
                        key={index}
                        onClick={() => addPresetSentenceFrame(frame)}
                        className="text-left p-3 bg-white border border-orange-200 rounded-lg hover:bg-orange-50 transition-colors"
                      >
                        <div className="text-sm font-mono text-orange-900">{frame}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Custom Sentence Frames */}
              <div className="space-y-2">
                {(differentiation.ell?.sentence_frames || []).map((frame, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <input
                      type="text"
                      value={frame}
                      onChange={(e) => updateSentenceFrame(index, e.target.value)}
                      placeholder={t('differentiation.sentenceFrameExample')}
                      className="input-field bg-white flex-1 font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => removeSentenceFrame(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {(differentiation.ell?.sentence_frames || []).length === 0 && (
                  <div className="text-center py-6 text-gray-500">
                    <p className="text-sm">{t('differentiation.noSentenceFrames')}</p>
                    <p className="text-xs mt-1">{t('differentiation.addLanguageStructures')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Visual Icons */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="text-sm font-medium text-gray-700">{t('differentiation.visualIcons')}</h5>
                  <p className="text-xs text-gray-500">{t('differentiation.nonVerbalCues')}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowPresets(prev => ({ ...prev, icons: !prev.icons }))}
                    className="btn-secondary flex items-center space-x-2"
                  >
                    <BookOpen className="w-4 h-4" />
                    <span>Presets</span>
                  </button>
                  <button
                    type="button"
                    onClick={addIcon}
                    className="btn-primary flex items-center space-x-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>{t('differentiation.addCustom')}</span>
                  </button>
                </div>
              </div>

              {/* Preset Icons */}
              {showPresets.icons && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h6 className="text-sm font-medium text-orange-900 mb-3">{t('differentiation.quickAddVisualIcons')}</h6>
                  <div className="flex flex-wrap gap-2">
                    {PRESET_ICONS.map((icon, index) => (
                      <button
                        key={index}
                        onClick={() => addPresetIcon(icon)}
                        className="px-3 py-2 bg-white border border-orange-200 rounded-lg hover:bg-orange-50 transition-colors text-sm font-medium text-orange-900"
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Custom Icons */}
              <div className="space-y-2">
                {(differentiation.ell?.icons || []).map((icon, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <input
                      type="text"
                      value={icon}
                      onChange={(e) => updateIcon(index, e.target.value)}
                      placeholder={t('differentiation.visualIconExample')}
                      className="input-field bg-white flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => removeIcon(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {(differentiation.ell?.icons || []).length === 0 && (
                  <div className="text-center py-6 text-gray-500">
                    <p className="text-sm">{t('differentiation.noVisualIcons')}</p>
                    <p className="text-xs mt-1">{t('differentiation.addNonVerbalCues')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
